<template>
  <div class="login-container fade-in">
    <!-- Toggle de Tema -->
    <ThemeToggle @theme-changed="onThemeChanged" />

    <!-- Card de Login -->
    <div class="login-card">
      <!-- Logo -->
      <div v-if="currentLogo" class="login-logo">
        <img 
          :src="currentLogo" 
          :alt="(settings && settings.siteName) || 'Logo'" 
          class="logo-image"
          decoding="async"
          fetchpriority="high"
          @error="handleImageError"
        >
      </div>
      
      <div class="login-input-group">
        <input 
          class="login-input" 
          type="text" 
          placeholder="Usuário" 
          v-model="user"
          @keyup.enter="login()"
          autocomplete="username"
        />
      </div>
      
      <div class="login-input-group">
        <input 
          class="login-input" 
          type="password" 
          placeholder="Senha" 
          v-model="password"
          @keyup.enter="login()"
          autocomplete="current-password"
        />
      </div>
      
      <button 
        type="button" 
        class="login-button" 
        @click="login()"
        :disabled="loading"
      >
        <span v-if="!loading" class="login-button-text">Login</span>
        <span v-else class="login-spinner-container">
          <i class="fa-solid fa-spinner fa-spin login-spinner"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import ThemeToggle from "@/components/ThemeToggle.vue";
import { getDefaultRoute, ROLES } from "@/services/permissions";
import {
  getCachedLoginSettings,
  setCachedLoginSettings,
  shouldUpdateLoginSettingsCache,
  preloadLogoImages,
} from "@/services/settingsLogoCache";

export default {
  components: {
    ThemeToggle
  },
  data: () => ({
    user: null,
    password: null,
    loading: false,
    settings: null,
    isDarkTheme: false,
  }),
  computed: {
    currentLogo() {
      if (!this.settings) return null;

      if (this.isDarkTheme) {
        return this.settings.logoDark || this.settings.logoLight || null;
      }
      return this.settings.logoLight || this.settings.logoDark || null;
    },
  },
  async mounted() {
    // Exibe logo do cache imediatamente (sem esperar a API)
    this.applyCachedSettings();
    this.checkCurrentTheme();

    // Atualiza settings em background e renova o cache
    this.loadSettings();
    
    // Observar mudanças de tema para atualizar logo
    this.themeObserver = new MutationObserver(() => {
      this.checkCurrentTheme();
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  },
  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    validateLogin() {
      if (
        this.user == null || this.user == "" ||
        this.password == null || this.password == ""
      ) {
        toast.error("Por favor, preencha todos os campos!");
        return false;
      }
      return true;
    },
    login() {
      if (this.validateLogin()) {
        this.loading = true;
        let request = {
          username: this.user,
          password: this.password,
        };

        mainService
          .login(request).then((response) => {
            localStorage.setItem("isAuthenticated", "true");
            
            // Salvar dados do usuário no localStorage
            if (response.data) {
              // Garantir que o role seja sempre um número válido
              let roleNumber = 0; // Default para ADMIN em caso de erro
              
              if (typeof response.data.role === 'number') {
                roleNumber = response.data.role;
              } else if (typeof response.data.role === 'string') {
                // Tentar converter de número primeiro
                if (!isNaN(response.data.role)) {
                  roleNumber = parseInt(response.data.role);
                } else {
                  // Se é string com nome do role, converter
                  const roleUpper = response.data.role.toUpperCase();
                  switch (roleUpper) {
                    case 'ADMIN': roleNumber = 0; break;
                    case 'GERENTE': roleNumber = 1; break;
                    case 'COBRADOR': roleNumber = 2; break;
                    case 'BANCA': roleNumber = 3; break;
                    case 'AGENTE': roleNumber = 4; break;
                    default: roleNumber = 0; // Default ADMIN
                  }
                }
              }
              
              const userData = {
                ...response.data,
                role: roleNumber
              };

              localStorage.setItem("user", JSON.stringify(userData));
            }
            
            toast.success("Login efetuado!");
            
            // Atualizar App.vue sem reload e navegar pela rota padrão do role
            window.dispatchEvent(new CustomEvent('authChanged', {
              detail: { authenticated: true, user: JSON.parse(localStorage.getItem('user') || '{}') }
            }));

            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            this.redirectBasedOnRole(userData);
          })
          .catch((error) => {
            this.password = null;
            toast.error("Login inválido!");
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    onThemeChanged(theme) {
      // Método chamado quando o tema muda
      this.checkCurrentTheme();
    },

    applyCachedSettings() {
      const cached = getCachedLoginSettings();
      if (cached) {
        this.settings = cached;
        preloadLogoImages(cached);
      }
    },

    async loadSettings() {
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          const fresh = response.data;
          const cached = getCachedLoginSettings();

          if (shouldUpdateLoginSettingsCache(cached, fresh)) {
            this.settings = fresh;
            setCachedLoginSettings(fresh);
          } else if (!this.settings) {
            this.settings = fresh;
          }

          preloadLogoImages(fresh);
        } else if (!this.settings) {
          this.settings = null;
        }
      } catch (error) {
        // Mantém cache se a API falhar
        if (!this.settings) {
          this.settings = null;
        }
      }
    },

    checkCurrentTheme() {
      this.isDarkTheme = document.documentElement.hasAttribute('data-theme') && 
                        document.documentElement.getAttribute('data-theme') === 'dark';
    },

    handleImageError(event) {
      // Fallback para a outra logo se disponível
      if (this.isDarkTheme && this.settings?.logoLight) {
        event.target.src = this.settings.logoLight;
      } else if (!this.isDarkTheme && this.settings?.logoDark) {
        event.target.src = this.settings.logoDark;
      }
    },

    async redirectBasedOnRole(userData) {
      const defaultRoute = (userData && userData.role !== undefined)
        ? getDefaultRoute(userData.role)
        : '/';

      // Aguardar o App.vue montar o layout autenticado antes de navegar
      await this.$nextTick();
      try {
        await this.$router.push(defaultRoute);
      } catch (error) {
        // Ignorar erro de navegação duplicada
        if (error?.name !== 'NavigationDuplicated') {
          console.error('Erro ao redirecionar após login:', error);
        }
      }
    }
  }
}
</script>

<style scoped>
.login-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  padding: 0.5rem;
}

.login-logo .logo-image {
  max-width: 250px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;
}

.login-logo .logo-image:hover {
  transform: scale(1.02);
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.15));
}

/* Responsivo */
@media (max-width: 768px) {
  .login-logo .logo-image {
    max-width: 200px;
    max-height: 60px;
  }
}

@media (max-width: 480px) {
  .login-logo {
    margin-bottom: 1.5rem;
  }
  
  .login-logo .logo-image {
    max-width: 180px;
    max-height: 50px;
  }
}

/* Estilos para o spinner de login */
.login-button {
  position: relative;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-button-text {
  display: inline-block;
}

.login-spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.login-spinner {
  font-size: 1.2rem;
  color: currentColor;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.login-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
